import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { Download, Newspaper, Trash2, Upload } from "lucide-react";
import { supabase } from "../../config/supabase";

const STORAGE_BUCKET = "newsletters";
const TABLE_NAME = "newsletters";

const publicUrlFor = (filePath) => {
	const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(filePath);
	return data?.publicUrl || "";
};

const Newsletters = () => {
	const [rows, setRows] = useState([]);
	const [subscribers, setSubscribers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deleteSubscriberOpen, setDeleteSubscriberOpen] = useState(false);
	const [selected, setSelected] = useState(null);
	const [selectedSubscriber, setSelectedSubscriber] = useState(null);
	const [form, setForm] = useState({ title: "", issue_label: "", file: null });
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const showMsg = (message, severity = "success") =>
		setSnackbar({ open: true, message, severity });

	const fetchAll = useCallback(async () => {
		try {
			const [newsRes, subRes] = await Promise.all([
				supabase
					.from(TABLE_NAME)
					.select("*")
					.order("sort_order", { ascending: true })
					.order("created_at", { ascending: false }),
				supabase
					.from("newsletter_subscribers")
					.select("id, email, created_at")
					.order("created_at", { ascending: false })
					.limit(200),
			]);
			if (newsRes.error) throw newsRes.error;
			if (subRes.error) throw subRes.error;
			setRows(newsRes.data || []);
			setSubscribers(subRes.data || []);
		} catch (err) {
			console.error("Failed to load newsletters:", err);
			showMsg(err.message || "Failed to load newsletters", "error");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAll();
	}, [fetchAll]);

	const openUpload = () => {
		setForm({ title: "", issue_label: "", file: null });
		setUploadOpen(true);
	};

	const handleUpload = async () => {
		if (!form.title.trim()) {
			showMsg("Title is required", "warning");
			return;
		}
		if (!form.file) {
			showMsg("Please choose a file", "warning");
			return;
		}

		setUploading(true);
		try {
			const safeName = form.file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
			const filePath = `${Date.now()}-${safeName}`;

			const { error: uploadError } = await supabase.storage
				.from(STORAGE_BUCKET)
				.upload(filePath, form.file, {
					contentType: form.file.type,
					upsert: false,
				});
			if (uploadError) throw uploadError;

			const { error: insertError } = await supabase.from(TABLE_NAME).insert({
				title: form.title.trim(),
				issue_label: (form.issue_label || "").trim() || null,
				file_path: filePath,
				file_name: form.file.name,
				sort_order: rows.length,
			});

			if (insertError) {
				await supabase.storage.from(STORAGE_BUCKET).remove([filePath]);
				throw insertError;
			}

			showMsg("Newsletter uploaded");
			setUploadOpen(false);
			setLoading(true);
			await fetchAll();
		} catch (err) {
			console.error("Upload failed:", err);
			showMsg(err.message || "Failed to upload newsletter", "error");
		} finally {
			setUploading(false);
		}
	};

	const handleDelete = async () => {
		if (!selected) return;
		setUploading(true);
		try {
			await supabase.storage.from(STORAGE_BUCKET).remove([selected.file_path]);
			const { error } = await supabase
				.from(TABLE_NAME)
				.delete()
				.eq("id", selected.id);
			if (error) throw error;
			showMsg("Newsletter deleted");
			setDeleteOpen(false);
			setSelected(null);
			setLoading(true);
			await fetchAll();
		} catch (err) {
			console.error("Delete failed:", err);
			showMsg(err.message || "Failed to delete newsletter", "error");
		} finally {
			setUploading(false);
		}
	};

	const handleDeleteSubscriber = async () => {
		if (!selectedSubscriber) return;
		const subscriberId = selectedSubscriber.id;
		setUploading(true);
		try {
			const { error } = await supabase
				.from("newsletter_subscribers")
				.delete()
				.eq("id", subscriberId);
			if (error) throw error;
			showMsg("Subscriber removed");
			setDeleteSubscriberOpen(false);
			setSelectedSubscriber(null);
			setSubscribers((prev) => prev.filter((s) => s.id !== subscriberId));
		} catch (err) {
			console.error("Failed to remove subscriber:", err);
			showMsg(err.message || "Failed to remove subscriber", "error");
		} finally {
			setUploading(false);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Newspaper size={28} />
					<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
						Newsletters
					</Typography>
				</Stack>
				<Button
					variant="contained"
					startIcon={<Upload size={16} />}
					onClick={openUpload}>
					Upload newsletter
				</Button>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Upload current and historic newsletters for download on the Who we are
				page. Subscribers from the public form are listed below.
			</Typography>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : (
				<Stack spacing={4}>
					<Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
						<Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
							<Typography variant="h6">Uploaded issues</Typography>
						</Box>
						{rows.length === 0 ? (
							<Box sx={{ p: 4, textAlign: "center" }}>
								<Typography color="text.secondary">
									No newsletters uploaded yet.
								</Typography>
							</Box>
						) : (
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Title</TableCell>
											<TableCell>Label</TableCell>
											<TableCell>File</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rows.map((row) => (
											<TableRow key={row.id}>
												<TableCell>{row.title}</TableCell>
												<TableCell>{row.issue_label || "—"}</TableCell>
												<TableCell>{row.file_name}</TableCell>
												<TableCell align="right">
													<IconButton
														aria-label="Download"
														href={publicUrlFor(row.file_path)}
														target="_blank"
														rel="noopener noreferrer">
														<Download size={16} />
													</IconButton>
													<IconButton
														color="error"
														aria-label="Delete"
														onClick={() => {
															setSelected(row);
															setDeleteOpen(true);
														}}>
														<Trash2 size={16} />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>

					<Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
						<Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
							<Typography variant="h6">
								Subscribers ({subscribers.length})
							</Typography>
						</Box>
						{subscribers.length === 0 ? (
							<Box sx={{ p: 4, textAlign: "center" }}>
								<Typography color="text.secondary">
									No subscribers yet.
								</Typography>
							</Box>
						) : (
							<TableContainer sx={{ maxHeight: 360 }}>
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell>Email</TableCell>
											<TableCell>Subscribed</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{subscribers.map((sub) => (
											<TableRow key={sub.id}>
												<TableCell>{sub.email}</TableCell>
												<TableCell>
													{new Date(sub.created_at).toLocaleString()}
												</TableCell>
												<TableCell align="right">
													<IconButton
														color="error"
														aria-label="Remove subscriber"
														onClick={() => {
															setSelectedSubscriber(sub);
															setDeleteSubscriberOpen(true);
														}}>
														<Trash2 size={16} />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>
				</Stack>
			)}

			<Dialog
				open={uploadOpen}
				onClose={() => !uploading && setUploadOpen(false)}
				fullWidth
				maxWidth="sm">
				<DialogTitle>Upload newsletter</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Title"
							value={form.title}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, title: e.target.value }))
							}
							placeholder="e.g. Funmiyo Newsletter — March 2026"
							fullWidth
							required
						/>
						<TextField
							label="Label (optional)"
							value={form.issue_label}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, issue_label: e.target.value }))
							}
							placeholder="e.g. Current · Mar 2026"
							fullWidth
						/>
						<Button variant="outlined" component="label" disabled={uploading}>
							{form.file ? form.file.name : "Choose PDF / file"}
							<input
								hidden
								type="file"
								accept=".pdf,.doc,.docx,application/pdf,image/*"
								onChange={(e) =>
									setForm((prev) => ({
										...prev,
										file: e.target.files?.[0] || null,
									}))
								}
							/>
						</Button>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUploadOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleUpload}
						disabled={uploading || !form.file || !form.title.trim()}
						startIcon={
							uploading ? <CircularProgress size={16} /> : <Upload size={16} />
						}>
						{uploading ? "Uploading..." : "Upload"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteOpen}
				onClose={() => !uploading && setDeleteOpen(false)}>
				<DialogTitle>Delete newsletter?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This removes <strong>{selected?.title}</strong> from the website and
						deletes the file from storage.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteOpen(false)} disabled={uploading}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDelete}
						disabled={uploading}>
						{uploading ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteSubscriberOpen}
				onClose={() => !uploading && setDeleteSubscriberOpen(false)}>
				<DialogTitle>Remove subscriber?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Remove <strong>{selectedSubscriber?.email}</strong> from the
						newsletter list? They can subscribe again later from the website.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteSubscriberOpen(false)}
						disabled={uploading}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDeleteSubscriber}
						disabled={uploading}>
						{uploading ? "Removing..." : "Remove"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default Newsletters;
